// Tree child-request mapper (Phase 3b) — PURE. The load-bearing bit of the Tree
// view: given a node's level + id, produce the scoped /breakdown call that fetches
// its children. One scoped call per expand (never the whole tree up front):
//
//   Account  → groupBy=campaign + accountIds=[thatAccount]
//   Campaign → groupBy=adset    + campaignId=thatCampaign
//   Ad set   → groupBy=ad       + adsetId=thatAdset
//   Ad       → leaf (no children)
//
// The account id to scope by is the row's attributed_kyck_account_id (what the
// account-level rows are keyed on = ReportingBreakdownRow.accountId). A node with a
// null id (e.g. an unattributed account) can't be scoped → treated as a leaf.

import type { ReportingDimension } from '../../types/reporting';

// The fixed Tree hierarchy, shallowest → deepest.
export const TREE_LEVELS: ReportingDimension[] = ['account', 'campaign', 'adset', 'ad'];

// The next level down, or null at the Ad leaf.
export function treeChildDim(dim: ReportingDimension): ReportingDimension | null {
  switch (dim) {
    case 'account':
      return 'campaign';
    case 'campaign':
      return 'adset';
    case 'adset':
      return 'ad';
    case 'ad':
      return null;
  }
}

// Account / Campaign / Ad set rows get an expand caret; the Ad level is a leaf.
export function treeHasCaret(dim: ReportingDimension): boolean {
  return treeChildDim(dim) !== null;
}

// The scope to merge onto the page filters for a child fetch. Exactly one field is
// set (the parent-id the endpoint scopes on).
export interface TreeChildScope {
  accountIds?: string[];
  campaignId?: string;
  adsetId?: string;
}

export interface TreeChildRequest {
  groupBy: ReportingDimension;
  scope: TreeChildScope;
}

// The child request for a node, or null when it has no children (Ad leaf, or an
// un-scopable null id).
export function childRequestFor(node: {
  dim: ReportingDimension;
  id: string | null;
}): TreeChildRequest | null {
  const childDim = treeChildDim(node.dim);
  if (childDim === null || node.id === null) return null;
  switch (node.dim) {
    case 'account':
      return { groupBy: childDim, scope: { accountIds: [node.id] } };
    case 'campaign':
      return { groupBy: childDim, scope: { campaignId: node.id } };
    case 'adset':
      return { groupBy: childDim, scope: { adsetId: node.id } };
    default:
      return null;
  }
}
